/**
 * Playbook → site-wide table export source.
 *
 * The Playbook shows one stat tab at a time, but a coach auditing "what can we
 * call?" wants every unit and category. So the export gathers the full option
 * list (enabled AND disabled) of every tab of every unit — the same lists the
 * page reads (`GET /playbook/v1/stats/{category}/{side}`) — and offers Units
 * and Categories as row filters in the dialog (the current unit pre-ticked).
 * One row per option: label, enabled / disabled, usage count, and more.
 */
import type { ApiClient } from '../../lib/api-client';
import type { StatOption } from '../../lib/api-types';
import type { ExportCell, ExportColumnDef, TableExportSource } from '../integrations/export-dialog';
import { exportFileStem, fileDay } from '../integrations/export-format';

import { PERSONNEL_CATEGORY, findDefaultPersonnel } from './default-personnel';
import {
  PLAYBOOK_TABS_BY_UNIT,
  SIDE_LABELS,
  playbookSide,
  statDefinition,
  type PlaybookUnit,
  type StatCategory,
} from './stat-catalog';

const UNITS: readonly PlaybookUnit[] = ['offense', 'defense', 'special_teams'];

export const PLAYBOOK_UNIT_LABELS: Readonly<Record<PlaybookUnit, string>> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

const DESIGN_LABELS: Readonly<Record<string, string>> = { run: 'Run', pass: 'Pass', rpo: 'RPO' };

const COLUMNS: readonly ExportColumnDef[] = [
  { field: 'unit', label: 'Unit', isVisible: true },
  { field: 'category', label: 'Category', isVisible: true },
  { field: 'side', label: 'Whose list', isVisible: true },
  { field: 'label', label: 'Option', isVisible: true },
  { field: 'status', label: 'Status', header: 'Enabled / disabled', isVisible: true },
  { field: 'usage_count', label: 'Usage count', header: 'Times used', isVisible: true },
  { field: 'source', label: 'Source', header: 'Stock / team', isVisible: true },
  { field: 'value', label: 'Stored code', isVisible: false },
  { field: 'design_type', label: 'Designed type', isVisible: false },
  { field: 'default_personnel', label: 'Default personnel', isVisible: false },
  { field: 'notes', label: 'Notes', isVisible: false },
];

interface PlaybookRow {
  unit: PlaybookUnit;
  category: StatCategory;
  option: StatOption;
  /** Formations: the tagged default personnel group's code (resolved from `personnel_group_id`). */
  defaultPersonnel?: string | null;
}

/** Every (unit, tab) of the Playbook with its derived side, in page order. */
export function playbookTabs(): { unit: PlaybookUnit; category: StatCategory }[] {
  return UNITS.flatMap((unit) =>
    PLAYBOOK_TABS_BY_UNIT[unit].map((category) => ({ unit, category }))
  );
}

export function playbookRowCells(row: PlaybookRow): ExportCell[] {
  const { option } = row;
  return [
    PLAYBOOK_UNIT_LABELS[row.unit],
    statDefinition(row.category).label,
    SIDE_LABELS[option.side],
    option.label,
    option.is_enabled ? 'Enabled' : 'Disabled',
    option.usage_count,
    option.is_stock ? 'Stock' : 'Team',
    option.value,
    option.design_type ? (DESIGN_LABELS[option.design_type] ?? option.design_type) : null,
    row.defaultPersonnel ?? null,
    option.notes,
  ];
}

export async function buildPlaybookExportSource(
  api: ApiClient,
  currentUnit: PlaybookUnit
): Promise<TableExportSource> {
  const tabs = playbookTabs();
  const lists = new Map<string, Promise<readonly StatOption[]>>();
  for (const tab of tabs) {
    const side = playbookSide(tab.unit, tab.category);
    const key = `${tab.category}:${side}`;
    if (!lists.has(key)) {
      lists.set(
        key,
        api
          .listStatOptions(tab.category, side)
          .then((page) => [...page.items].sort((left, right) => left.sort_order - right.sort_order))
      );
    }
  }
  const rows: PlaybookRow[] = [];
  for (const tab of tabs) {
    const side = playbookSide(tab.unit, tab.category);
    const options = await (lists.get(`${tab.category}:${side}`) ?? Promise.resolve([]));
    const personnel = await (lists.get(`${PERSONNEL_CATEGORY}:${side}`) ?? Promise.resolve([]));
    for (const option of options) {
      const defaultPersonnel = findDefaultPersonnel(option, personnel)?.value ?? null;
      rows.push({ unit: tab.unit, category: tab.category, option, defaultPersonnel });
    }
  }
  const categories = [...new Set(tabs.map((tab) => tab.category))];

  return {
    page: 'playbook',
    pageLabel: 'Playbook',
    columns: COLUMNS,
    rows: () => rows.map(playbookRowCells),
    fileStem: exportFileStem('Playbook', null, fileDay(null)),
    sheetName: 'Playbook',
    rowNoun: { one: 'option', many: 'options' },
    rowFilters: [
      {
        id: 'unit',
        label: 'Units',
        options: UNITS.map((unit) => ({ value: unit, label: PLAYBOOK_UNIT_LABELS[unit] })),
        valueOf: (index) => rows[index]?.unit ?? '',
        initial: [currentUnit],
      },
      {
        id: 'category',
        label: 'Categories',
        options: categories.map((category) => ({
          value: category,
          label: statDefinition(category).label,
        })),
        valueOf: (index) => rows[index]?.category ?? '',
      },
    ],
  };
}
